import React, { ChangeEvent, useState } from "react";
import {
  IUCNStatus,
  PopulationTrend,
  Species,
  SpeciesCategory,
} from "../common/ObjectTypes";
import { AddIcon, DeleteIcon } from "../common/ProjectIcons";
import TextInput from "../common/TextInput";
import SelectInput from "../common/SelectInput";
import Pagination from "../common/Pagination";
import { Link } from "react-router-dom";
import { getCurrentUser } from "../../helpers/authHeader";
import tailwindClassnames from "../common/TaildwindClassnames";

type SpeciesListTypes = {
  species: Species[];
  populationTrends: PopulationTrend[];
  speciesCategories: SpeciesCategory[];
  iucnStatuses: IUCNStatus[];
  onSelectSpecies: Function;
  onClickAdd: Function;
  onDelete: Function;
  pageCount: number;
  pageSize: number;
  totalCount: number;
  handlePageClick: (selectedItem: { selected: number }) => void;
  reloadPage: boolean;
  recordStart: number;
  recordEnd: number;
  onSearch: Function;
  onChangeSearch: Function;
  // subStr: string;
  searchQuery: any;
  loading: boolean;
};

const SpeciesList = ({
  species,
  populationTrends,
  speciesCategories,
  iucnStatuses,
  onSelectSpecies,
  onClickAdd,
  pageCount,
  pageSize,
  totalCount,
  handlePageClick,
  reloadPage,
  recordStart,
  recordEnd,
  searchQuery,
  onSearch,
  onChangeSearch,
  // subStr,
  loading,
}: SpeciesListTypes) => {
  const getPopulationTrendById = (code: any) => {
    let trendName = populationTrends?.find((trend) => trend.id === code);
    return trendName?.name || null;
  };

  const getSpeciesCategoryById = (code: any) => {
    let categoryName = speciesCategories?.find(
      (category) => category.id === code
    );
    return categoryName?.name || null;
  };

  const getIUCNStatusById = (code: any) => {
    let statusName = iucnStatuses?.find((status) => status.id === code);
    return statusName?.name || null;
  };

  const currentUser = getCurrentUser();

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-gray-800">Species</h1>
      </div>

      {/* Search + Add Button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 w-full max-w-md">
          <TextInput
            name=""
            label=""
            onChange={onChangeSearch}
            value={searchQuery.name}
            placeholder="Filter by name"
            error={null}
            disabled={false}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-tealBlue-400 focus:ring focus:ring-tealBlue-200"
            type="text"
            onKeyPress={(e) => {
              if (e.key === "Enter") onSearch();
            }}
            initialFocus={false}
          />
          <button
            onClick={() => onSearch()}
            className="px-4 py-2 rounded-full text-sm font-medium text-white bg-tealBlue-500 hover:bg-tealBlue-600 shadow transition"
          >
            Search
          </button>
        </div>

        {currentUser.isSystemAdmin && (
          <Link
            to="/species/add"
            className="px-4 py-2 rounded-full text-sm font-medium text-white bg-tealBlue-500 hover:bg-tealBlue-600 shadow transition"
          >
            + Add
          </Link>
        )}
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
        <table className="w-full border-collapse text-sm text-gray-700">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-600">
                Name
              </th>
              <th className="px-4 py-3 text-left font-medium text-gray-600">
                Colloquial Name
              </th>
              <th className="px-4 py-3 text-left font-medium text-gray-600">
                Scientific Name
              </th>
              <th className="px-4 py-3 text-left font-medium text-gray-600">
                Description
              </th>
              <th className="px-4 py-3 text-left font-medium text-gray-600">
                IUCN Status
              </th>
              <th className="px-4 py-3 text-left font-medium text-gray-600">
                Population Trend
              </th>
              <th className="px-4 py-3 text-left font-medium text-gray-600">
                Species Category
              </th>
            </tr>
          </thead>

          <tbody>
            {species.map((s: Species, index: number) => {
              const rowClass = index % 2 === 0 ? "bg-white" : "bg-gray-50";

              return (
                <tr
                  key={s.speciesId}
                  className={`${rowClass} hover:bg-gray-100 transition cursor-pointer`}
                  onClick={() => onSelectSpecies(s.speciesId)}
                >
                  <td className="px-4 py-3 font-medium text-gray-900 w-48">
                    {s.name}
                  </td>
                  <td className="px-4 py-3">{s.colloquialName}</td>
                  <td className="px-4 py-3 italic">{s.scientificName}</td>
                  <td className="px-4 py-3 w-1/2">
                    <p className="text-gray-600 line-clamp-2">
                      {s.description?.substring(0, 200)}
                      {s.description && s.description.length > 200 ? "..." : ""}
                    </p>
                  </td>
                  <td className="px-4 py-3 w-40">
                    {getIUCNStatusById(s.iucnStatusId)}
                  </td>
                  <td className="px-4 py-3">
                    {getPopulationTrendById(s.populationTrendId)}
                  </td>
                  <td className="px-4 py-3">
                    {getSpeciesCategoryById(s.speciesCategoryId)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <Pagination
        handlePageClick={handlePageClick}
        pageCount={pageCount}
        recordStart={recordStart}
        recordEnd={recordEnd}
        totalCount={totalCount}
        loading={loading}
        pageSize={searchQuery.pageSize}
        selectPage={false}
        searchQuery={searchQuery}
      />
    </div>
  );
};

export default SpeciesList;
